import { Splitter, SplitterPanel } from 'primereact/splitter';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { ExitSlipModalType, selectedExitSlipModal } from '../../redux/reducers/exitSlipModalSlice';
import ExitSlipModalFilters, {
  availablesOptions,
  DropdownItemModel,
  initAvailablesOptions,
} from './ExitSlipModalFilters';
import { PreloadedQuery, useQueryLoader, useLazyLoadQuery, useRelayEnvironment, fetchQuery } from 'react-relay';
import { getAllUnitsGraphQL } from '../../graphQLOperations/Dashboard';
import { DashboardAllUnitsQuery } from '../../graphQLOperations/__generated__/DashboardAllUnitsQuery.graphql';
import { SitesTargetGroupsQuery } from '../../graphQLOperations/__generated__/SitesTargetGroupsQuery.graphql';
import { sitesTargetGroupsQuery } from '../../graphQLOperations/Sites';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { useGetClassesByRole } from '../../custom-hooks/useGetClassesByRole';
import { sortByKey } from '../../businessLogicService/sortCriteria';
import ExitSlipModalFooter from './ExitSlipModalFooter';
import { ExitSlipCurriculumLessonsQuery } from '../../graphQLOperations/__generated__/ExitSlipCurriculumLessonsQuery.graphql';
import {
  getAssignedExitSlipLessons,
  getClassExitSlipDueOnByClasses,
  getCurriculumLessonsByFilter,
  getExitSlipLessonRoadmaps,
} from '../../graphQLOperations/ExitSlip';
import { selectedExitSlipFilter, setExitSlipFilter } from '../../redux/reducers/filterExitSlipSlice';
import { selectedTeacherDashboardFilter } from '../../redux/reducers/teacherDashboardFiltersSlice';
import ExitSlipModalBody from './ExitSlipModalBody';
import { Toast } from 'primereact/toast';
import ExitSlipModalToast from './ExitSlipModalToast';
import removeTargetDuplicates from '../../businessLogicService/removeTargetDuplicates';
import { ExitSlipClassExitSlipDueOnByClassesQuery } from '../../graphQLOperations/__generated__/ExitSlipClassExitSlipDueOnByClassesQuery.graphql';
import { ExitSlipLessonRoadmapsQuery } from '../../graphQLOperations/__generated__/ExitSlipLessonRoadmapsQuery.graphql';
import { ExitSlipAssignedLessonsQuery } from '../../graphQLOperations/__generated__/ExitSlipAssignedLessonsQuery.graphql';
import {
  activeFocusRoadmaps,
  assignedLessonIdsFrom,
  narrowRoadmapOptions,
  partitionRoadmaps,
  resolveExitSlipPrefill,
} from '../../businessLogicService/exitSlipModalPrefill';
import { classById } from '../../graphQLOperations/Class';
import { ClassByIdQuery } from '../../graphQLOperations/__generated__/ClassByIdQuery.graphql';
import { bucketStudentsByRoadmap, resolveDefaultRoadmapId } from '../TeacherDashboard/unitFocusBuckets';
import { selectActiveFocusClassIds, selectFocuses } from '../../redux/reducers/focusSlice';
import { useStoreFirstRefresh } from '../../custom-hooks/useStoreFirstRefresh';
export interface LessonDate {
  date: Date | null;
  curriculumLessonId: string;
  classExitSlipId?: string;
  isReassinged?: boolean;
  isEdited?: boolean;
}

export const ExitSlipModalContainer: FC = () => {
  const toast = useRef<Toast | null>(null);
  const dispatch = useAppDispatch();
  const userRedux = useAppSelector(selectUser);
  const exitSlipFilterRedux = useAppSelector(selectedExitSlipFilter);
  const teacherDashboardFilters = useAppSelector(selectedTeacherDashboardFilter);
  const focuses = useAppSelector(selectFocuses);
  const focusClassIds = useAppSelector(selectActiveFocusClassIds);
  const { type } = useAppSelector(selectedExitSlipModal);
  const prefilled = useRef<boolean>(false);
  const resolvedForClass = useRef<string>('');
  const { refresh, supersede } = useStoreFirstRefresh();
  const environment = useRelayEnvironment();
  const dashboardClassId = teacherDashboardFilters.classes?.[0]?.id ?? '';
  const selectedClassId = exitSlipFilterRedux.classes[0] ?? '';
  const activeClassId = selectedClassId || dashboardClassId;

  const [saveButtonDisabled, setSaveButtonDisabled] = useState<boolean>(true);
  const [applyRefetchSnapshot, setApplyRefetchSnapshot] = useState<boolean>(false);

  const [availablesOptions, setAvailablesOptions] = useState<availablesOptions>(initAvailablesOptions);
  const [noDataYet, setNoDataYet] = useState<boolean>(true);
  const [lessonDates, setLessonDates] = useState<LessonDate[]>([]);

  const [queryReference, loadQuery] = useQueryLoader<ExitSlipCurriculumLessonsQuery>(getCurriculumLessonsByFilter);
  const [queryDisabledDatesReference, loadDisabledDatesQuery] =
    useQueryLoader<ExitSlipClassExitSlipDueOnByClassesQuery>(getClassExitSlipDueOnByClasses);

  const sitesData = useLazyLoadQuery<SitesTargetGroupsQuery>(sitesTargetGroupsQuery, {
    sitesFilter: { userId: `UserNode:${userRedux?.id}` },
  });
  const allUnits = useLazyLoadQuery<DashboardAllUnitsQuery>(getAllUnitsGraphQL, {
    isActive: 'ACTIVE',
  });
  const exitSlipLessons = useLazyLoadQuery<ExitSlipLessonRoadmapsQuery>(
    getExitSlipLessonRoadmaps,
    {},
    { fetchPolicy: 'store-or-network' },
  );
  const assignedLessons = useLazyLoadQuery<ExitSlipAssignedLessonsQuery>(
    getAssignedExitSlipLessons,
    { filter: { classIds: activeClassId ? [activeClassId] : [] }, skip: !activeClassId },
    { fetchPolicy: 'store-or-network' },
  );
  const activeClassData = useLazyLoadQuery<ClassByIdQuery>(
    classById,
    { classId: activeClassId, skip: !activeClassId },
    { fetchPolicy: 'store-or-network' },
  );
  const { allClassesData, allClassesLoaded } = useGetClassesByRole(true, true);

  useEffect(() => {
    const classesTemplates = allClassesData.map(el => ({
      value: el.id,
      label: el.section ? el.name + ' - ' + el.section : el.name,
    }));
    setAvailablesOptions(prev => ({ ...prev, classes: classesTemplates }));
  }, [allClassesData]);

  const panelReady = !!exitSlipFilterRedux.classes.length && exitSlipFilterRedux.roadmap !== '';

  //both panel queries are driven off the same filter, so a refresh that rebuilt its own variables
  //could quietly load a different slice than the one on screen
  const lessonsVariables: ExitSlipCurriculumLessonsQuery['variables'] = {
    filter: {
      hasExitSlip: true,
      isActive: 'ACTIVE',
      targetGroupId: exitSlipFilterRedux.roadmap,
    },
  };
  const dueOnVariables: ExitSlipClassExitSlipDueOnByClassesQuery['variables'] = {
    filter: {
      classIds: exitSlipFilterRedux.classes,
    },
  };

  useEffect(() => {
    //TEAMB-3482: Unit is optional, so the fetch is roadmap-wide and the body scopes to a unit
    //on the client — changing Unit never costs a refetch
    supersede();
    if (panelReady) {
      loadQuery(lessonsVariables, { fetchPolicy: 'network-only' });
      loadDisabledDatesQuery(dueOnVariables, { fetchPolicy: 'network-only' });
    }
    setNoDataYet(!panelReady);
    //not keyed on unit — it only scopes the rendered list
  }, [exitSlipFilterRedux.classes, exitSlipFilterRedux.roadmap]);

  //TEAMB-3422 a mutation fired from inside the panel changes the server's answer to both panel
  //queries while the filter stays put, so the effect above cannot carry the refresh: re-dispatching
  //a spread of the filter leaves classes and roadmap identical and its deps never trip.
  //A store-first refresh keeps the list on screen — loading straight from the network suspends the
  //body to its ThreeDotLoader on every cancel.
  const refreshPanel = () => {
    if (!panelReady) return;
    refresh(
      [
        fetchQuery(environment, getCurriculumLessonsByFilter, lessonsVariables).toPromise(),
        fetchQuery(environment, getClassExitSlipDueOnByClasses, dueOnVariables).toPromise(),
      ],
      fetchPolicy => {
        loadQuery(lessonsVariables, { fetchPolicy });
        loadDisabledDatesQuery(dueOnVariables, { fetchPolicy });
      },
    );
  };

  //partitionRoadmaps walks every exit ticket lesson in the curriculum to answer which roadmaps
  //hold one. lessonDates lives here and a date pick in the panel sets it, so without the memo
  //that whole walk re-runs on every click.
  const { withUnassigned, withAssigned, roadmapOptions, prefillRoadmapOptions } = useMemo(() => {
    const roadmapTemplate: DropdownItemModel[] =
      sitesData?.sites?.nodes
        ?.map(site => site?.targetGroups?.map(target => ({ value: target?.id ?? '', label: target?.name ?? '' })) ?? [])
        ?.flat()
        ?.sort(sortByKey('label')) ?? [];
    const { withTickets, withUnassigned, withAssigned } = partitionRoadmaps(
      exitSlipLessons.curriculumLessons,
      assignedLessonIdsFrom(assignedLessons.classExitSlips),
    );
    const roadmapOptions = narrowRoadmapOptions(removeTargetDuplicates(roadmapTemplate), withTickets);

    return {
      withUnassigned,
      withAssigned,
      roadmapOptions,
      prefillRoadmapOptions: narrowRoadmapOptions(
        roadmapOptions,
        type === ExitSlipModalType.EDIT && withAssigned.size ? withAssigned : withTickets,
      ),
    };
  }, [sitesData, exitSlipLessons, assignedLessons, type]);

  useEffect(() => {
    setAvailablesOptions(prev => ({ ...prev, roadmap: roadmapOptions }));
  }, [sitesData, exitSlipLessons, assignedLessons, type]);

  const defaultRoadmapId = resolveDefaultRoadmapId(
    activeFocusRoadmaps(focuses, allUnits.allTargets, focusClassIds, activeClassId),
    bucketStudentsByRoadmap(activeClassData.class?.users),
  );

  const buildPrefill = (classIds: readonly string[]) =>
    resolveExitSlipPrefill({
      dashboardClassIds: classIds,
      classOptions: allClassesData.map(cl => ({ value: cl.id })),
      roadmapOptions: prefillRoadmapOptions,
      preferredRoadmapId: defaultRoadmapId,
      fallbackRoadmapIds: !activeClassId ? undefined : type === ExitSlipModalType.EDIT ? withAssigned : withUnassigned,
    });

  useEffect(() => {
    if (prefilled.current || !allClassesLoaded) return;
    prefilled.current = true;
    const { roadmap, unit, classes } = exitSlipFilterRedux;
    if (roadmap || unit || classes.length) {
      resolvedForClass.current = classes[0] ?? '';
      return;
    }

    const seeded = buildPrefill(teacherDashboardFilters.classes?.map(cl => cl.id) ?? []);
    resolvedForClass.current = seeded.classes[0] ?? '';
    dispatch(setExitSlipFilter(seeded));
  }, [allClassesLoaded]);

  // Edit only. There, roadmap and unit describe what the class already has, so a new class
  // means a new answer. Assign is the opposite: the teacher picks a roadmap and unit to
  // assign INTO, and its roadmap list is curriculum-wide rather than class-scoped, so
  // re-resolving there just discarded a selection they had already made.
  useEffect(() => {
    if (type !== ExitSlipModalType.EDIT) return;
    if (!prefilled.current || !selectedClassId || resolvedForClass.current === selectedClassId) return;
    resolvedForClass.current = selectedClassId;

    const { roadmap, unit } = buildPrefill([selectedClassId]);
    dispatch(setExitSlipFilter({ ...exitSlipFilterRedux, roadmap, unit }));
  }, [selectedClassId, type]);

  return (
    <Splitter
      style={{
        height: '100vh',
        position: 'fixed',
        width: '100vw',
        zIndex: 5,
        backgroundColor: 'transparent',
      }}
    >
      <SplitterPanel style={{ backgroundColor: 'black', opacity: 0.3 }}></SplitterPanel>
      <SplitterPanel size={65} style={{ backgroundColor: 'white', opacity: 1, maxWidth: '900', padding: 0 }}>
        <div className="p-0 m-0 w-full flex flex-column" style={{ overflowY: 'scroll' }}>
          <div>
            <ExitSlipModalFilters
              allUnits={allUnits}
              availablesOptions={availablesOptions}
              setAvailablesOptions={setAvailablesOptions}
              modalType={type}
              applyRefetchSnapshot={applyRefetchSnapshot}
            />
          </div>
          <div style={{ overflowY: 'auto' }}>
            <ExitSlipModalBody
              queryReference={queryReference as PreloadedQuery<ExitSlipCurriculumLessonsQuery, Record<string, unknown>>}
              queryDisabledDatesReference={
                queryDisabledDatesReference as PreloadedQuery<
                  ExitSlipClassExitSlipDueOnByClassesQuery,
                  Record<string, unknown>
                >
              }
              noDataYet={noDataYet}
              setNoDataYet={setNoDataYet}
              lessonDates={lessonDates}
              setLessonDates={setLessonDates}
              modalType={type}
              setSaveButtonDisabled={setSaveButtonDisabled}
              toast={toast}
              setApplyRefetchSnapshot={setApplyRefetchSnapshot}
              refreshPanel={refreshPanel}
            />
          </div>
          <ExitSlipModalFooter
            lessonDates={lessonDates}
            setLessonDates={setLessonDates}
            modalType={type}
            toast={toast}
            setSaveButtonDisabled={setSaveButtonDisabled}
            saveButtonDisabled={saveButtonDisabled}
            setApplyRefetchSnapshot={setApplyRefetchSnapshot}
            applyRefetchSnapshot={applyRefetchSnapshot}
            refreshPanel={refreshPanel}
          />
        </div>
        <ExitSlipModalToast toastRef={toast} />
      </SplitterPanel>
    </Splitter>
  );
};
